import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import leavingService from '../../services/leavingService';
import LeavingCertificate from '../../components/LeavingCertificate';
import PrintInstructions from '../../components/common/PrintInstructions';

const PRINT_MODES = [
  { value: 'printed', label: '🖨 Printed Format', desc: 'Values only — load pre-printed TC form in printer tray.' },
  { value: 'plain', label: '📄 Plain Paper', desc: 'Full certificate with labels, boxes and college header on blank A4.' },
];

export default function LeavingPrint() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printMode, setPrintMode] = useState('printed');
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await leavingService.getById(id);
        setRecord(res.data);
      } catch (err) {
        toast.error(err.message);
        navigate('/leaving');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const handleModeChange = (mode) => {
    setPrintMode(mode);
    if (mode === 'plain') setPreviewMode(false);
  };

  const handlePrint = () => {
    setPreviewMode(false);
    setTimeout(() => window.print(), 150);
  };

  if (loading) return <div className="loading-center"><div className="spinner" />Loading certificate...</div>;
  if (!record) return null;

  const printData = {
    serialNo: record.serialNo,
    lastSchoolAttended: record.lastSchoolAttended,
    progress: record.progress,
    conduct: record.conduct,
    dateOfAdmission: record.dateOfAdmission,
    admissionSerialNo: record.admissionSerialNo,
    standard: record.standard,
    since: record.since,
    dateOfLeaving: record.dateOfLeaving,
    reasonOfLeaving: record.reasonOfLeaving,
    remarks: record.remark,
    dateOfIssue: record.dateOfIssue,
    enrollmentNo: record.student?.enrollmentNo,
    aadharNo: record.student?.aadharNo,
    studentName: record.student?.fullName,
    fatherName: record.student?.fatherName,
    motherName: record.student?.motherName,
    nationality: record.student?.nationality,
    motherTongue: record.student?.motherTongue,
    religion: record.student?.religion,
    caste: record.student?.caste,
    subCaste: record.student?.subCaste,
    placeOfBirth: record.student?.placeOfBirth,
    district: record.student?.district,
    birthState: record.student?.birthState,
    dob: record.student?.dob,
    dobInWords: record.student?.dobInWords,
  };

  const modeInfo = PRINT_MODES.find((m) => m.value === printMode);

  return (
    <div>
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button className="btn btn-outline" onClick={() => navigate('/leaving')}>← Back</button>

        <div style={{ flex: 1 }}>
          <h1 className="page-title">Leaving Certificate — {record.student?.fullName}</h1>
          <p className="page-subtitle">Serial No. {record.serialNo} · {record.standard} · Leaving: {record.dateOfLeaving}</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Print Mode</label>
          <select value={printMode} onChange={(e) => handleModeChange(e.target.value)} className="form-control" style={{ width: '200px', fontSize: 13, fontWeight: 500 }}>
            {PRINT_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        {printMode === 'printed' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={previewMode} onChange={(e) => setPreviewMode(e.target.checked)} />
            Show template (calibration)
          </label>
        )}

        <button className="btn btn-outline" onClick={() => navigate(`/leaving/${id}/edit`)}>✏️ Edit</button>
        <button className="btn btn-success" onClick={handlePrint}>🖨️ Print Certificate</button>
      </div>

      <PrintInstructions />

      <div className="no-print" style={{ background: printMode === 'plain' ? '#f0f9ff' : '#f0fdf4', border: `1px solid ${printMode === 'plain' ? '#7dd3fc' : '#86efac'}`, borderRadius: 8, padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: 13, color: printMode === 'plain' ? '#0c4a6e' : '#14532d' }}>
        <strong>{modeInfo?.label}</strong> — {modeInfo?.desc}
        {printMode === 'printed' && previewMode && <span style={{ marginLeft: '1rem', color: '#854d0e', fontWeight: 600 }}>⚠️ Calibration ON — turn OFF before printing</span>}
      </div>

      <LeavingCertificate
        data={printData}
        printMode={printMode}
        previewMode={previewMode}
        college={record.college}
        templateConfig={record.college?.leavingTemplate}
      />
    </div>
  );
}